import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import StoryMedia from '../stories/StoryMedia';
import StoryVideoPlayer from '../media/StoryVideoPlayer';
import useMediaQuery from '../../utils/useMediaQuery';
import { T, RADIUS, BODY_FONT, formatTime } from './messagesModel';
import { viewerPosition, viewerDurationMs } from './albumModel';
import useSwipeToDismiss from '../../hooks/useSwipeToDismiss';

const HOLD_MS = 220;
const TICK_MS = 50;
/** The reply bar's height (40px gradient + 44px pill + 14px): the player's scrub strip sits above it. */
const REPLY_BAR_H = 98;

/**
 * The album viewer (handoff §4): the items sent together play like a story reel — only
 * those, nothing before or after. One 2px progress segment per item; the sender's square
 * avatar, name and time over `PHOTO 3 OF 5`; a PAUSED chip while held, paused or typing;
 * pause / play and, on a clip, mute. Tap the left third to go back, anywhere else to go on;
 * hold to pause; ← / → / Space / Esc with a keyboard. A photo holds eight seconds, a clip
 * its window. The last item ends → back to the thread.
 *
 * On someone else's album a reply bar sits at the bottom: the words go as a text message
 * quoting the album, the viewer stays open, and a toast says REPLY SENT.
 *
 * Desktop: a 9:16 stage on #1A1A1A with prev / next circles beside it. Phone: full screen.
 * Built on StoryMedia (the one player) rather than forking StoryViewer; the chrome is
 * redrawn here to its measurements. A clip plays in `StoryVideoPlayer` (§4b): tap-to-skip on
 * the ramp, the hidden scrub bar with its thumbnail preview, the first-video tips — the
 * player owns the clip's taps and holds and reports time; this draws the progress segment.
 */
export default function AlbumViewer({ msg, index = 0, mine = false, senderName = '', senderAvatarUrl = null, onClose, onReply = null, onToast = null }) {
  const items = useMemo(() => (msg.albumItems || []).filter((i) => i.status !== 'failed'), [msg.albumItems]);
  const [idx, setIdx] = useState(Math.min(Math.max(0, index), Math.max(0, items.length - 1)));
  const [t, setT] = useState(0);
  const [paused, setPaused] = useState(false);
  const [holding, setHolding] = useState(false);
  const [typing, setTyping] = useState(false);
  const [muted, setMuted] = useState(true);
  const [coach, setCoach] = useState(false);
  const [reply, setReply] = useState('');
  const phone = useMediaQuery('(max-width: 767px)');
  const mediaRef = useRef(null);
  const downAt = useRef(null);
  const downX = useRef(0);
  const downW = useRef(1);
  const holdTimer = useRef(null);
  const cur = items[idx] || null;
  const isVideo = cur?.kind === 'video';
  const halted = paused || holding || typing;

  const step = useCallback((d) => {
    setT(0);
    setIdx((i) => {
      const n = i + d;
      if (n < 0) return 0;
      if (n >= items.length) { onClose?.(); return i; }
      return n;
    });
    mediaRef.current?.restart?.();
  }, [items.length, onClose]);

  // The clock: a photo runs on the tick; a clip reports its own time through the player.
  useEffect(() => {
    if (!cur || halted || isVideo) return undefined;
    const iv = setInterval(() => {
      setT((prev) => {
        const next = prev + TICK_MS;
        if (next >= viewerDurationMs(cur)) { step(1); return 0; }
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(iv);
  }, [cur, halted, step, isVideo]);

  useEffect(() => {
    const onKey = (e) => {
      if (typing || coach) return;
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === ' ') { e.preventDefault(); setPaused((p) => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [typing, coach, step, onClose]);

  useEffect(() => () => clearTimeout(holdTimer.current), []);

  // Swipe the album down to close it, the way the portal viewer does. Declared above the
  // early return — hooks run in the same order on every render.
  const frameRef = useRef(null);
  const swipe = useSwipeToDismiss(frameRef, { onDismiss: () => onClose?.(), enabled: phone });

  if (!cur) return null;

  const onDown = (e) => {
    downAt.current = Date.now();
    const box = e.currentTarget.getBoundingClientRect();
    downW.current = box.width || 1;
    downX.current = e.clientX - box.left;
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => setHolding(true), HOLD_MS);
  };
  const onUp = () => {
    clearTimeout(holdTimer.current);
    if (swipe.claimed.current) { downAt.current = null; setHolding(false); return; }
    if (downAt.current == null) return;
    const quick = Date.now() - downAt.current < HOLD_MS;
    downAt.current = null;
    if (holding) { setHolding(false); return; }
    if (quick) step(downX.current < downW.current / 3 ? -1 : 1);
  };

  const sendReply = () => {
    const body = reply.trim();
    if (!body || !onReply) return;
    onReply(body, { item: cur, index: idx, total: items.length });
    setReply('');
    onToast?.('REPLY SENT');
  };

  const mediaItem = { id: cur.id, kind: cur.kind, mediaUrl: cur.url, thumbnailUrl: cur.thumbnailUrl, trimStartMs: cur.trimStartMs, trimEndMs: cur.trimEndMs, durationSec: cur.durationSec, width: cur.width, height: cur.height, storyboardUrl: cur.storyboardUrl };
  const segs = items.map((_, i) => (i < idx ? 100 : i === idx ? Math.min(100, (t / viewerDurationMs(cur)) * 100) : 0));
  const showReply = !mine && !!onReply;
  const chromeVis = coach ? 'hidden' : 'visible';
  const circle = { width: 44, height: 44, borderRadius: '50%', border: '2px solid rgba(255,255,255,.4)', background: 'transparent', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer', transition: 'background-color 200ms, color 200ms' };

  const stage = (
    <div
      data-testid="album-viewer-stage"
      style={phone
        ? { position: 'absolute', inset: 0, background: '#000', overflow: 'hidden' }
        : { position: 'relative', height: 'min(90vh, 860px)', aspectRatio: '9 / 16', maxWidth: 'calc(100vw - 160px)', background: '#000', borderRadius: 14, overflow: 'hidden' }}
    >
      {isVideo ? (
        <StoryVideoPlayer
          key={cur.id || cur.url}
          item={mediaItem}
          active
          muted={muted}
          paused={paused || typing}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          onEnded={() => step(1)}
          onTime={(sec) => setT(sec * 1000)}
          onHoldChange={setHolding}
          onCoachChange={setCoach}
          bottomInset={showReply ? REPLY_BAR_H : 0}
          edgeZones
        />
      ) : (
        <>
          <StoryMedia ref={mediaRef} item={mediaItem} active muted={muted} paused={halted} fit="contain" loop={false} onEnded={() => step(1)} />
          <div onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={onUp} style={{ position: 'absolute', inset: 0, zIndex: 5, cursor: 'pointer', touchAction: 'none' }} />
        </>
      )}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 150, background: 'linear-gradient(to bottom, rgba(0,0,0,.75), rgba(0,0,0,0))', pointerEvents: 'none', zIndex: 6, visibility: chromeVis }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: phone ? 'calc(10px + env(safe-area-inset-top, 0px))' : 10, padding: '0 8px', zIndex: 7, pointerEvents: 'none', visibility: chromeVis }}>
        <div className="flex" style={{ gap: 4 }} data-testid="album-viewer-progress">
          {segs.map((w, i) => (
            <div key={i} style={{ flex: 1, height: 2, background: 'rgba(255,255,255,.35)' }}><div style={{ height: '100%', background: '#fff', width: `${w}%` }} /></div>
          ))}
        </div>
        <div className="flex justify-between items-start" style={{ marginTop: 14, padding: '0 8px', pointerEvents: 'auto' }}>
          <div className="flex items-start" style={{ gap: 10 }}>
            <span style={{ width: 34, height: 34, border: '2px solid #fff', boxSizing: 'border-box', background: T.activeBadge, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {senderAvatarUrl ? <img src={senderAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span className="font-mono font-bold" style={{ fontSize: 12 }}>{(senderName || '?').charAt(0).toUpperCase()}</span>}
            </span>
            <span className="flex flex-col" style={{ gap: 3, color: '#fff' }}>
              <span style={{ fontSize: 13, fontFamily: BODY_FONT }}><span style={{ fontWeight: 700 }}>{mine ? 'you' : (senderName || '').toLowerCase()}</span><span style={{ marginLeft: 8, color: 'rgba(255,255,255,.7)' }}>{formatTime(msg.createdAt)}</span></span>
              <span className="font-mono font-bold" data-testid="album-viewer-position" style={{ fontSize: 10, letterSpacing: '.1em', color: 'rgba(255,255,255,.7)' }}>{viewerPosition(cur, idx, items.length)}</span>
            </span>
          </div>
          <div className="flex items-center" style={{ gap: 14, color: '#fff' }}>
            {halted && (
              <span className="font-mono font-bold flex items-center" data-testid="album-viewer-paused" style={{ gap: 6, height: 26, padding: '0 12px', background: 'rgba(0,0,0,.72)', borderRadius: RADIUS.pill, fontSize: 10, letterSpacing: '.12em' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="4" width="5" height="16" /><rect x="14" y="4" width="5" height="16" /></svg>PAUSED
              </span>
            )}
            <button type="button" onClick={() => setPaused((p) => !p)} aria-label={paused ? 'Play' : 'Pause'} style={{ background: 'none', border: 0, padding: 0, color: '#fff', display: 'flex', cursor: 'pointer' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d={paused ? 'M6 3l15 9-15 9z' : 'M6 4h4v16H6zM14 4h4v16h-4z'} /></svg>
            </button>
            {cur.kind === 'video' && (
              <button type="button" onClick={() => setMuted((m) => !m)} aria-label={muted ? 'Unmute' : 'Mute'} style={{ background: 'none', border: 0, padding: 0, color: '#fff', display: 'flex', cursor: 'pointer' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square"><path d="M4 9h4l5-4v14l-5-4H4z" /><path d={muted ? 'M16 9l5 6M21 9l-5 6' : 'M16 8a5 5 0 0 1 0 8M19 5a9 9 0 0 1 0 14'} /></svg>
              </button>
            )}
          </div>
        </div>
      </div>
      {showReply && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 7, padding: phone ? '40px 12px calc(14px + env(safe-area-inset-bottom, 0px))' : '40px 12px 14px', background: 'linear-gradient(to top, rgba(0,0,0,.8), rgba(0,0,0,0))', display: 'flex', gap: 8, alignItems: 'center', visibility: chromeVis }}>
          <input
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onFocus={() => setTyping(true)}
            onBlur={() => setTyping(false)}
            onKeyDown={(e) => { if (e.key === 'Enter') sendReply(); if (e.key === 'Escape') e.target.blur(); }}
            placeholder={`Reply to ${(senderName || '').split(' ')[0].toLowerCase() || 'them'}…`}
            aria-label="Reply"
            style={{ flex: 1, minWidth: 0, height: 44, borderRadius: RADIUS.pill, border: '2px solid rgba(255,255,255,.55)', background: 'transparent', color: '#fff', padding: '0 18px', fontFamily: BODY_FONT, fontSize: 15, outline: 'none' }}
          />
          <button type="button" onClick={sendReply} aria-label="Send reply" style={{ width: 44, height: 44, borderRadius: '50%', border: 0, background: '#fff', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.5 2.5L11 13" /><path d="M21.5 2.5l-6.8 19-3.7-8.5L2.5 9.3z" /></svg>
          </button>
        </div>
      )}
      {phone && (
        <button type="button" onClick={onClose} aria-label="Close" style={{ position: 'absolute', right: 12, top: 'calc(58px + env(safe-area-inset-top, 0px))', zIndex: 8, width: 36, height: 36, border: 0, background: 'rgba(0,0,0,.45)', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      )}
    </div>
  );

  return createPortal(
    <div ref={frameRef} role="dialog" aria-modal="true" aria-label="Album viewer" data-testid="album-viewer" className="pr-overlay-root" style={{ position: 'fixed', inset: 0, zIndex: 1200, background: phone ? '#000' : '#1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'center', userSelect: 'none', ...swipe.style }}>
      {!phone && (
        <button type="button" onClick={onClose} aria-label="Close" className="hover:bg-white hover:text-black" style={{ position: 'absolute', right: 24, top: 20, zIndex: 3, width: 40, height: 40, border: '2px solid rgba(255,255,255,.4)', background: 'transparent', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      )}
      {!phone && (
        <button type="button" onClick={() => step(-1)} aria-label="Previous" className="hover:bg-white hover:text-black" style={{ ...circle, marginRight: 24, visibility: idx > 0 ? 'visible' : 'hidden' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
      )}
      {stage}
      {!phone && (
        <button type="button" onClick={() => step(1)} aria-label="Next" className="hover:bg-white hover:text-black" style={{ ...circle, marginLeft: 24 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M9 5l7 7-7 7" /></svg>
        </button>
      )}
    </div>,
    document.body,
  );
}
